/**
 * "Install app" — the affordance, and the iOS instructions Safari forces us to write.
 *
 *   <InstallAppButton />   a toolbar button; renders nothing once installed
 *   <InstallAppCard />     a dismissible card for the sign-in page
 *   <UpdateBanner />       offered when a new version is waiting; never auto-applied
 *
 * Chrome and Edge give us a real prompt. Safari gives us nothing at all, so on iOS we
 * explain the Share → Add to Home Screen path instead of pretending a button exists.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
  DeviceMobile, DownloadSimple, X, Export, PlusSquare, ArrowClockwise,
} from '@phosphor-icons/react';
import {
  canInstall, promptInstall, watchInstallability, isInstalled, isIos,
  hasUpdate, watchUpdates, applyUpdate,
} from '../../lib/pwa';

const DISMISSED_KEY = 'lms_install_dismissed';

function useInstallState() {
  const [state, setState] = useState(() => ({
    installed: isInstalled(),
    available: canInstall(),
    ios: isIos(),
  }));
  useEffect(() => watchInstallability(() => setState((s) => ({ ...s, available: canInstall(), installed: isInstalled() }))), []);
  return state;
}

/** The Safari steps, shown inline rather than in a dialog. */
function IosSteps() {
  return (
    <ol className="space-y-1.5 text-[12px] text-muted-foreground" data-testid="ios-install-steps">
      <li className="flex items-start gap-2">
        <Export className="w-4 h-4 flex-shrink-0 mt-0.5 text-shell-primary" />
        <span>Tap <strong>Share</strong> at the bottom of Safari.</span>
      </li>
      <li className="flex items-start gap-2">
        <PlusSquare className="w-4 h-4 flex-shrink-0 mt-0.5 text-shell-primary" />
        <span>Choose <strong>Add to Home Screen</strong>.</span>
      </li>
      <li className="flex items-start gap-2">
        <DeviceMobile className="w-4 h-4 flex-shrink-0 mt-0.5 text-shell-primary" />
        <span>Open it from your home screen — it runs full screen, without the browser bars.</span>
      </li>
    </ol>
  );
}

/** Toolbar button. Hidden once the app is installed or if the browser cannot install it. */
export function InstallAppButton({ className = '' }) {
  const { installed, available, ios } = useInstallState();
  const [showIos, setShowIos] = useState(false);

  if (installed) return null;
  if (!available && !ios) return null;

  if (ios && !available) {
    return (
      <div className={`relative ${className}`}>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-white hover:bg-white/10"
          onClick={() => setShowIos((v) => !v)} data-testid="install-app-btn">
          <DownloadSimple className="w-4 h-4" /> <span className="hidden sm:inline">Install app</span>
        </Button>
        {showIos && (
          <div className="absolute right-0 top-full mt-1 z-50 w-[280px] rounded-lg border border-border bg-white p-3 shadow-lg text-foreground"
            data-testid="ios-install-pop">
            <div className="flex items-start gap-2 mb-2">
              <p className="text-[13px] font-semibold mr-auto">Add to your home screen</p>
              <button type="button" onClick={() => setShowIos(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
            <IosSteps />
          </div>
        )}
      </div>
    );
  }

  return (
    <Button variant="ghost" size="sm" className={`h-8 gap-1.5 text-white hover:bg-white/10 ${className}`}
      onClick={promptInstall} data-testid="install-app-btn">
      <DownloadSimple className="w-4 h-4" /> <span className="hidden sm:inline">Install app</span>
    </Button>
  );
}

/** A card for the sign-in page, where there is room to explain what installing gets you. */
export function InstallAppCard({ orgName }) {
  const { installed, available, ios } = useInstallState();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });

  const dismiss = useCallback(() => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* private mode */ }
  }, []);

  if (installed || dismissed) return null;
  if (!available && !ios) return null;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2" data-testid="install-app-card">
      <div className="flex items-start gap-2">
        <DeviceMobile className="w-5 h-5 text-shell-primary flex-shrink-0 mt-0.5" weight="duotone" />
        <div className="min-w-0 mr-auto">
          <p className="text-[13px] font-semibold">Install {orgName || 'the app'}</p>
          <p className="field-hint">Opens full screen from your home screen, and keeps working on a weak connection.</p>
        </div>
        <button type="button" onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label="Not now">
          <X className="w-4 h-4" />
        </button>
      </div>
      {available ? (
        <Button size="sm" className="w-full" onClick={promptInstall} data-testid="install-app-card-btn">
          <DownloadSimple className="w-4 h-4" /> Install
        </Button>
      ) : <IosSteps />}
    </div>
  );
}

/** A new build is cached and waiting. We ask rather than reloading under them. */
export function UpdateBanner() {
  const [ready, setReady] = useState(hasUpdate());
  useEffect(() => watchUpdates(() => setReady(true)), []);
  if (!ready) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-shell-primary text-white text-[12px]" data-testid="update-banner">
      <ArrowClockwise className="w-4 h-4 flex-shrink-0" />
      <span className="mr-auto">A new version is ready.</span>
      <button type="button" onClick={applyUpdate} className="font-semibold underline underline-offset-2" data-testid="apply-update">
        Reload
      </button>
    </div>
  );
}

export default InstallAppButton;
